import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { CreateSnippetPayload } from '../../services/blockServiceTypes.ts';
import { isBodyTooLargeError } from '../../services/blockServiceTypes.ts';
import { messages as baseMessages } from '../../modals/SnippetBaseModal.i18n.ts';
import { addSnippetQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import { reportError } from '../../reportError.ts';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { clearSnippetsCache } from '../../services/snippetsCache.ts';
import { SnippetBodyFormat, SnippetProduct } from '../../snippetTypes.ts';
import type { Snippet } from '../../snippetTypes.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import { shareActions, type ShareSnippetOptions } from './shareActions.ts';

/**
 * Returns the appropriate error flag title and description MessageDescriptors based on whether
 * the error is a body-too-large error. Uses shared base messages for too-large errors and
 * falls back to the provided action-specific messages otherwise.
 */
export const getSnippetErrorFlagMessages = (
	error: Error,
	actionErrorFlagTitle: MessageDescriptor,
	actionErrorFlagDescription: MessageDescriptor,
): { flagTitle: MessageDescriptor; flagDescription: MessageDescriptor } => {
	const isBodyTooLarge = isBodyTooLargeError(error);
	return {
		flagTitle: isBodyTooLarge ? baseMessages.contentTooLargeFlagTitle : actionErrorFlagTitle,
		flagDescription: isBodyTooLarge
			? baseMessages.contentTooLargeFlagDescription
			: actionErrorFlagDescription,
	};
};

/**
 * Shows a success flag with the given title and description.
 */
export const showSnippetSuccessFlag = (
	flags: WithFlagsProps['flags'],
	title: string,
	description?: string,
	actions?: Parameters<WithFlagsProps['flags']['showSuccessFlag']>[0]['actions'],
): void => {
	if (!flags) return;
	flags.showSuccessFlag({
		title,
		description: description ?? '',
		isAutoDismiss: true,
		...(actions && { actions }),
	});
};

/**
 * Shows an error flag with the given title and description.
 */
export const showSnippetErrorFlag = (
	flags: WithFlagsProps['flags'],
	title: string,
	description?: string,
): void => {
	if (!flags) return;
	flags.showErrorFlag({
		title,
		description: description ?? '',
		isAutoDismiss: true,
	});
};

/**
 * Builds a CreateSnippetPayload for the create snippet API from name, ADF body, and optional description.
 */
export function buildCreatePayload(
	name: string,
	body: ADFEntity,
	description?: string,
): CreateSnippetPayload {
	const descriptionValue = description?.trim();
	return {
		name,
		...(descriptionValue && { description: descriptionValue }),
		body: JSON.stringify(body),
		bodyFormat: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
	};
}

export type AddSnippetToQuickInsertAndShareOptions = Pick<
	ShareSnippetOptions,
	'flags' | 'intl' | 'editorAPI'
> & {
	isShared?: boolean;
};

export type AddSnippetToQuickInsertAndShareParams = {
	snippet: Snippet;
	options: AddSnippetToQuickInsertAndShareOptions;
	storeApi: StoreActionApi<SnippetsState>;
	onShareFailure?: (snippetId: string, error: Error) => void;
};

/**
 * Adds snippet to quick insert when editorAPI is set, and shares the snippet when site sharing
 * is enabled and isShared is true. Call after create or duplicate succeeds.
 */
export async function addSnippetToQuickInsertAndShare({
	snippet,
	options,
	storeApi,
	onShareFailure,
}: AddSnippetToQuickInsertAndShareParams): Promise<void> {
	const { getState, dispatch } = storeApi;
	const { flags, intl, editorAPI, isShared } = options;
	if (editorAPI) {
		addSnippetQuickInsertItem({ snippet, editorAPI });
	}
	if (getState().isSnippetSharingForSiteEnabled && isShared) {
		await dispatch(
			shareActions.shareSnippet(snippet.snippetId, {
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				onShareFailure,
			}),
		);
	}
}

export type SnippetSaveOptions = {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	isShared?: boolean;
	onShareFailure?: (snippetId: string, error: Error) => void;
	onSaveFailure?: (error: Error) => void;
	successFlagActions?: Parameters<WithFlagsProps['flags']['showSuccessFlag']>[0]['actions'];
};

export type SnippetSaveMessages = {
	successFlagTitle: MessageDescriptor;
	errorFlagTitle: MessageDescriptor;
	errorFlagDescription: MessageDescriptor;
};

/**
 * Shared implementation for create and duplicate snippet flows. Creates a snippet via the API,
 * refreshes the list, shows success/error flags, and triggers quick insert + sharing.
 */
export async function saveSnippetWithUI(
	name: string,
	body: ADFEntity,
	description: string | undefined,
	options: SnippetSaveOptions | undefined,
	messages: SnippetSaveMessages,
	storeApi: StoreActionApi<SnippetsState>,
): Promise<Snippet | null> {
	const { getState, setState } = storeApi;
	const params = buildCreatePayload(name, body, description);

	setState({ isLoading: true });

	try {
		const snippet = await BlockServiceClient.createSnippet(params);
		const isSnippetSharingForSiteEnabled = getState().isSnippetSharingForSiteEnabled;

		clearSnippetsCache();
		const updatedSnippets = await BlockServiceClient.fetchSnippets({
			isSnippetSharingForSiteEnabled,
		});

		setState({
			snippets: updatedSnippets,
			isLoading: false,
			isLoaded: true,
		});

		if (options) {
			const { flags, intl, editorAPI, isShared, onShareFailure, successFlagActions } = options;
			showSnippetSuccessFlag(
				flags,
				intl.formatMessage(messages.successFlagTitle, { snippetName: snippet.name }),
				intl.formatMessage(baseMessages.successFlagDescription),
				successFlagActions,
			);
			await addSnippetToQuickInsertAndShare({
				snippet,
				options: { flags, intl, editorAPI, isShared },
				storeApi,
				onShareFailure,
			});
		}
		return snippet;
	} catch (thrownError) {
		const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));

		setState({ isLoading: false });
		reportError(err);

		if (options) {
			options.onSaveFailure?.(err);
			const { flagTitle, flagDescription } = getSnippetErrorFlagMessages(
				err,
				messages.errorFlagTitle,
				messages.errorFlagDescription,
			);
			showSnippetErrorFlag(
				options.flags,
				options.intl.formatMessage(flagTitle),
				options.intl.formatMessage(flagDescription),
			);
			return null;
		}
		throw err;
	}
}
